import React, { useState, useEffect, useCallback, useMemo  } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebaseConfig';
import styles from '../styles/PageStore.module.css';
import NavBar from '../NavBar.js';
import { useNavigate } from 'react-router-dom';

import netflixLogo from '../../assets/netflix.png';
import disneyLogo from '../../assets/disney.png';
import hboLogo from '../../assets/hbomax.png';
import primeLogo from '../../assets/primevideo.png';
import spotifyLogo from '../../assets/spotify.png';
import ytpLogo from '../../assets/youtubepremium.png';
import { useSearch } from '../../context/SearchContext';

const PageStore = () => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('usuario');
  const [userCountry, setUserCountry] = useState(null);
  const [stockByPlatform, setStockByPlatform] = useState({});
  const [platformLogos, setPlatformLogos] = useState({}); // Nuevo estado para logos dinámicos

  const navigate = useNavigate();
  const { searchQuery } = useSearch();

  // Función para obtener los logos de Firebase
  const loadPlatformLogos = async () => {
    try {
      const plataformasSnapshot = await getDocs(collection(db, 'plataformas'));
      const logosMap = {};
      
      plataformasSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.nombre && data.imagen) {
          logosMap[data.nombre] = data.imagen;
        }
      });
      
      setPlatformLogos(logosMap);
      console.log('Logos cargados desde Firebase:', logosMap);
    } catch (error) {
      console.error('Error cargando logos de plataformas:', error);
      // Si hay error, usar logos por defecto
      setPlatformLogos({});
    }
  };

  // Logos de plataformas locales como fallback
  const LOCAL_LOGOS = {
    'Netflix': netflixLogo,
    'Disney+': disneyLogo,
    'HBO Max': hboLogo,
    'Prime Video': primeLogo,
    'Spotify Premium': spotifyLogo,
    'YouTube Premium': ytpLogo,
  };

  // Función para obtener el logo correcto (Firebase primero, local como fallback)
  const getLogo = (platformName) => {
    // Primero buscar en los logos de Firebase
    if (platformLogos[platformName]) {
      return platformLogos[platformName];
    }
    
    // Si no existe en Firebase, usar el logo local
    if (LOCAL_LOGOS[platformName]) {
      return LOCAL_LOGOS[platformName];
    }
    
    // Si no hay logo disponible, retornar null
    return null;
  };

const navigateToMediosPagos = () => {
  const check = validateCart(cart);
  if (!check.ok) {
    window.alert(check.reason);
    return;
  }
  const total = getTotalPrice();
  const cartFormatted = cart.map(item => ({
    plataforma: item.nombre,
    pantallas: item.pantallas,
    unidad: item.unidad.toLowerCase(),
    cantidad: item.quantity,
    precio: item.precio,
    id: item.id
  }));
  navigate('/medios-pago', { state: { total, cart: cartFormatted } });
};

const filteredProductos = useMemo(() => {
  let result = productos;
  
  // Primero aplicar filtros de búsqueda
  if (searchQuery && searchQuery.trim() !== '') {
    const query = searchQuery.toLowerCase().trim();
    
    result = productos.filter(producto => {
      // Buscar en nombre de la plataforma
      const matchesName = producto.nombre.toLowerCase().includes(query);
      // Buscar en etiqueta, calidad, unidad y descripción
      const matchesEtiqueta = producto.etiqueta.toLowerCase().includes(query);
      const matchesCalidad = producto.calidad.toLowerCase().includes(query);
      const matchesUnidad = producto.unidad.toLowerCase().includes(query);
      const matchesDescripcion = producto.descripcion.some(desc =>
        desc.toLowerCase().includes(query)
      );

      // Combinación de condiciones de búsqueda
      const matchesTerms = (
        (query.includes('stream') && matchesName) ||
        (query.includes('música') && producto.nombre.toLowerCase().includes('spotify')) ||
        (query.includes('video') && (producto.nombre.toLowerCase().includes('youtube') || producto.nombre.toLowerCase().includes('prime'))) ||
        (query.includes('película') && matchesName) ||
        (query.includes('premium') && matchesEtiqueta) ||
        (query.includes('4k') || query.includes('hd')) && matchesCalidad
      );

      return matchesName || matchesEtiqueta || matchesCalidad || matchesUnidad || matchesDescripcion || matchesTerms;
    });
  }

  // Luego ordenar: populares primero, luego alfabéticamente
  return result.sort((a, b) => {
    // Si uno es popular y el otro no, el popular va primero
    if (a.popular && !b.popular) return -1;
    if (!a.popular && b.popular) return 1;
    
    // Si ambos son populares o ambos no son populares, ordenar alfabéticamente
    return a.nombre.localeCompare(b.nombre);
  });
}, [productos, searchQuery]);



  // Función para formatear precios según la moneda
  const formatPrice = (price, moneda) => {
    if (!price || isNaN(price)) {
      return moneda === 'COP' ? '$0' : '$0.00';
    }
    
    if (moneda === 'COP') {
      return `$${Math.round(price).toLocaleString('es-CO')}`;
    } else {
      return `$${price.toFixed(2)}`;
    }
  };

  // Función para determinar si el país usa pesos colombianos
  const isCOP = (country) => {
    if (country == null) return false;
    const cleaned = country.toString().normalize('NFKC').trim().toUpperCase();
    return cleaned === '+57' || cleaned === '57' || cleaned === 'COLOMBIA' || cleaned === 'CO' || cleaned === 'COL';
  };

  // Función para obtener el rol y país del usuario
  const getUserData = async (uid) => {
    try {
      const userQuery = query(
        collection(db, 'Usuarios'),
        where('uid', '==', uid)
      );
      const userSnapshot = await getDocs(userQuery);
      
      if (!userSnapshot.empty) {
        const userData = userSnapshot.docs[0].data();
        return {
          rol: userData.rol || 'usuario',
          country: userData.country || null
        };
      } else {
        return {
          rol: 'usuario',
          country: null
        };
      }
    } catch (error) {
      console.error('Error obteniendo datos del usuario:', error);
      return {
        rol: 'usuario',
        country: null
      };
    }
  };

  // Función para calcular stock disponible por plataforma y pantallas
  const calculateStock = async () => {
    try {
      const cuentasQuery = query(
        collection(db, 'Cuentas'),
        where('propietario', '!=', 'Gojo')
      );
      const cuentasSnapshot = await getDocs(cuentasQuery);
      
      const stockMap = new Map();
      
      cuentasSnapshot.docs.forEach(doc => {
        const cuenta = doc.data();
        
        if (!cuenta.plataforma || !cuenta.pantallas) {
          return;
        }
        
        const key = `${cuenta.plataforma}-${cuenta.pantallas}`;
        
        if (!stockMap.has(key)) {
          stockMap.set(key, {
            plataforma: cuenta.plataforma,
            pantallas: parseInt(cuenta.pantallas) || 1,
            count: 0,
            cuentas: []
          });
        }
        
        const existing = stockMap.get(key);
        existing.count += 1;
        existing.cuentas.push({
          codigo: cuenta.codigo,
          pantallas: isNaN(parseInt(cuenta.pantallas)) ? 0 : parseInt(cuenta.pantallas)

        });
        stockMap.set(key, existing);
      });

      return stockMap;
    } catch (error) {
      console.error('Error calculando stock:', error);
      return new Map();
    }
  };

  // Función para obtener catálogo según el rol
  const getCatalogo = async (rol) => {
    try {
      const catalogoCollection = rol === 'vendedor' ? 'catalogoVendedores' : 'catalogo';
      const catalogoSnapshot = await getDocs(collection(db, catalogoCollection));
      
      return catalogoSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error obteniendo catálogo:', error);
      return [];
    }
  };

const loadProductos = useCallback(async () => {
  if (!user?.uid || userCountry === null) {
    return;
  }

  setLoading(true);
  try {
    const usarCOP = isCOP(userCountry);
    const monedaActual = usarCOP ? 'COP' : 'USD';

    // Obtener stock y catálogo en paralelo
    const [stockMap, catalogoItems] = await Promise.all([
      calculateStock(),
      getCatalogo(userRole)
    ]);

    const stockResume = {};
    stockMap.forEach((value) => {
      const { plataforma, pantallas, count } = value;
      if (!stockResume[plataforma]) stockResume[plataforma] = {};
      stockResume[plataforma][pantallas] = (stockResume[plataforma][pantallas] || 0) + count;
    });
    setStockByPlatform(stockResume);

    const productosConStock = catalogoItems.map(item => {
      let disponibles = 0;
      const pantallasItem = parseInt(item.pantallas || '1');

      if (item.unidad === 'completa') {
        const key = `${item.plataforma}-${pantallasItem}`;
        const stock = stockMap.get(key);
        disponibles = stock ? stock.count : 0;
      } else if (item.unidad === 'pantalla') {
        let totalPantallasDisponibles = 0;
        stockMap.forEach((stock, key) => {
          if (stock.plataforma === item.plataforma) {
            totalPantallasDisponibles += stock.count * stock.pantallas;
          }
        });
        if (pantallasItem > 0) {
          disponibles = Math.floor(totalPantallasDisponibles / pantallasItem);
        }
      }

      let precioStr, precioBaseStr, porcentajeDescuento;
      if (usarCOP) {
        precioStr = item.precioConDescuentoCOP || item.precioBaseCOP || '0';
        precioBaseStr = item.precioBaseCOP || '0';
        porcentajeDescuento = item.porcentajeDescuentoCOP;
      } else {
        precioStr = item.precioConDescuentoUSD || item.precioBaseUSD || '0';
        precioBaseStr = item.precioBaseUSD || '0';
        porcentajeDescuento = item.porcentajeDescuentoUSD;
      }

      const precio = parseFloat(precioStr) || 0;
      const precioOriginal = parseFloat(precioBaseStr) || 0;
      const tieneDescuento = precioOriginal > precio && precio > 0;

      return {
        id: item.id,
        nombre: item.plataforma,
        calidad: item.calidad || 'HD',
        pantallas: pantallasItem,
        descripcion: Array.isArray(item.descripcion) ? item.descripcion : [],
        precio,
        moneda: monedaActual,
        unidad: item.unidad === 'completa' ? 'Completa' :
                item.unidad === 'pantalla' ? 'Pantalla' : 'Pantalla',
        precioOriginal: tieneDescuento ? precioOriginal : null,
        duracion: item.duracion,
        etiqueta: item.etiqueta || 'Premium',
        popular: item.popular === true,
        disponibles,
        porcentajeDescuento
      };
    }).filter(producto => producto.disponibles > 0 && producto.precio > 0);

    setProductos(productosConStock);
  } catch (error) {
    console.error('Error cargando productos:', error);
    // Mostrar un mensaje de error y no vaciar la lista completamente
    setProductos([]);
    alert("Hubo un problema al cargar los productos. Inténtalo nuevamente.");
  } finally {
    setLoading(false);
  }
}, [user?.uid, userCountry, userRole]);


  // Efecto para manejar autenticación
  useEffect(() => {
    const handleAuthStateChange = async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        
        try {
          const userData = await getUserData(currentUser.uid);
          setUserRole(userData.rol);
          setUserCountry(userData.country);
        } catch (error) {
          console.error('Error obteniendo datos del usuario:', error);
          setUserRole('usuario');
          setUserCountry(null);
        }
      } else {
        setUser(null);
        setUserRole('usuario');
        setUserCountry(null);
        setProductos([]);
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);
    return () => unsubscribe();
  }, []);

  // Efecto para cargar logos al montar el componente
  useEffect(() => {
    loadPlatformLogos();
  }, []);

  // Efecto para cargar productos cuando cambian las dependencias
  useEffect(() => {
    if (user?.uid && userRole && userCountry !== null) {
      loadProductos();
    }
  }, [loadProductos]);

  // Agrupa demanda del carrito por plataforma
const summarizeCartByPlatform = (items) => {
  // Estructura:
  // {
  //   [plataforma]: {
  //     completeBySize: { [pantallas]: qtyCompleta },
  //     screensDemand: totalPantallasSueltas // en pantallas (no en "paquetes")
  //   }
  // }
  const result = {};
  for (const it of items) {
    const plataforma = it.nombre;
    const size = parseInt(it.pantallas || 1);
    const qty = parseInt(it.quantity || 0);
    const isComplete = (it.unidad || '').toLowerCase() === 'completa';
    const isScreen  = (it.unidad || '').toLowerCase() === 'pantalla';

    if (!result[plataforma]) {
      result[plataforma] = { completeBySize: {}, screensDemand: 0 };
    }

    if (isComplete) {
      result[plataforma].completeBySize[size] = (result[plataforma].completeBySize[size] || 0) + qty;
    } else if (isScreen) {
      // Pantallas sueltas: consumen 'size' pantallas por unidad
      result[plataforma].screensDemand += (qty * size);
    }
  }
  return result;
};

// Valida combinaciones imposibles entre "Completa" y "Pantalla"
const validateCart = (nextCart) => {
  const summary = summarizeCartByPlatform(nextCart);

  for (const plataforma of Object.keys(summary)) {
    const platformStock = stockByPlatform[plataforma] || {}; // { [size]: availableAccounts }
    const { completeBySize, screensDemand } = summary[plataforma];

    // 1) No exceder cuentas completas por tamaño
    for (const sizeStr of Object.keys(completeBySize)) {
      const size = parseInt(sizeStr);
      const needComplete = completeBySize[size] || 0;
      const haveAccounts = platformStock[size] || 0;
      if (needComplete > haveAccounts) {
        return {
          ok: false,
          reason: `No hay suficientes cuentas completas de ${plataforma} (${size} pantallas). Pedidas: ${needComplete}, disponibles: ${haveAccounts}.`
        };
      }
    }

    // 2) Pantallas sueltas no pueden superar lo que sobra después de reservar las completas
    // Máximo de pantallas sueltas disponibles = Σ (accounts[size] - completeReserved[size]) * size
    let maxScreensForLoose = 0;
    for (const sizeStr of Object.keys(platformStock)) {
      const size = parseInt(sizeStr);
      const haveAccounts = platformStock[size] || 0;
      const reservedComplete = completeBySize[size] || 0;
      const leftoverAccounts = Math.max(0, haveAccounts - reservedComplete);
      maxScreensForLoose += leftoverAccounts * size;
    }

    if (screensDemand > maxScreensForLoose) {
      return {
        ok: false,
        reason: `No hay suficientes pantallas disponibles en ${plataforma} tras reservar las cuentas completas. Máximo para pantallas: ${maxScreensForLoose}, pedidas: ${screensDemand}.`
      };
    }
  }

  return { ok: true };
};


  // Funciones del carrito
const addToCart = (product) => {
  setCart(prev => {
    const existing = prev.find(i => i.id === product.id);
    let proposal;
    if (existing) {
      const inc = Math.min(existing.quantity + 1, product.disponibles);
      proposal = prev.map(i => i.id === product.id ? { ...i, quantity: inc } : i);
    } else {
      proposal = [...prev, { ...product, quantity: 1 }];
    }

    const check = validateCart(proposal);
    if (!check.ok) {
      window.alert(check.reason);
      return prev; // no aplicar cambio
    }
    return proposal;
  });
};


  const removeFromCart = (id) => {
    setCart(prevCart => prevCart.filter(item => item.id !== id));
  };

const updateQuantity = (id, newQuantity) => {
  const product = productos.find(p => p.id === id);
  const maxQuantity = product ? product.disponibles : 1;

  if (newQuantity <= 0) {
    setCart(prev => prev.filter(i => i.id !== id));
    return;
  }

  const bounded = Math.min(newQuantity, maxQuantity);

  setCart(prev => {
    const proposal = prev.map(i => i.id === id ? { ...i, quantity: bounded } : i);
    const check = validateCart(proposal);
    if (!check.ok) {
      window.alert(check.reason);
      return prev; // revertir
    }
    return proposal;
  });
};


  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.precio * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const currentMoneda = isCOP(userCountry) ? 'COP' : 'USD';

  // Render condicional mejorado
  if (!user) {
    return (
      <>
        <NavBar />
        <div className={styles.storeWrapper}>
          <div style={{ textAlign: 'center', color: '#cbd5e1', padding: '2rem' }}>
            <p>Por favor, inicia sesión para ver los productos disponibles.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <div className={styles.storeWrapper}>
        {/* Header */}
        <header className={styles.header}>
          <button 
            className={`${styles.cartButton} ${isCartOpen ? styles.cartButtonRaised : ''}`}
            onClick={() => setIsCartOpen(true)}
            aria-label="Abrir carrito"
          >
            <svg className={styles.icon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="8" cy="21" r="1"/>
              <circle cx="19" cy="21" r="1"/>
              <path d="m2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
            </svg>
            <span className="text"></span>
            {getTotalItems() > 0 && (
              <span className={styles.cartBadge}>{getTotalItems()}</span>
            )}
          </button>
        </header>


        {/* AGREGAR ESTE BLOQUE COMPLETO: */}
        {searchQuery && (
          <div style={{ 
            textAlign: 'center', 
            padding: '1rem', 
            backgroundColor: 'rgba(59, 130, 246, 0.1)', 
            borderRadius: '8px', 
            margin: '1rem 0',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <p style={{ color: '#3b82f6', margin: 0 }}>
              Mostrando {filteredProductos.length} resultado{filteredProductos.length !== 1 ? 's' : ''} 
              para "<strong>{searchQuery}</strong>"
              {filteredProductos.length === 0 && (
                <span style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
                  Intenta con otros términos como "Netflix", "Spotify", "Premium", etc.
                </span>
              )}
            </p>
          </div>
        )}

    

        {/* Products Grid */}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#cbd5e1', padding: '2rem' }}>
            Cargando productos…
          </div>
        ) : (
          <div className={styles.grid}>
           {filteredProductos.length === 0 ? (
              <div style={{ 
                gridColumn: '1 / -1',
                textAlign: 'center', 
                color: '#cbd5e1',
                padding: '2rem'
              }}>
                {searchQuery ? (
                  <div>
                    <p>No se encontraron productos que coincidan con tu búsqueda.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
                      Sugerencias: Intenta buscar por nombre de plataforma (Netflix, Spotify), 
                      calidad (HD, 4K) o tipo (Premium, Completa, Pantalla).
                    </p>
                  </div>
                ) : (
                  <p>No hay productos disponibles en este momento.</p>
                )}
              </div>
            ) : (
              filteredProductos.map((producto) => {
                const logoUrl = getLogo(producto.nombre);
                
                return (
                  <div
                    key={producto.id}
                    className={`${styles.card} ${producto.popular ? styles.popular : ''}`}
                  >
                    {producto.popular && (
                      <div className={styles.popularBadge}>
                        <span>★</span>
                        Popular
                      </div>
                    )}

                    <div className={styles.cardHeader}>
                      <div className={styles.serviceLogo}>
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={producto.nombre}
                            className={styles.serviceImg}
                            loading="lazy"
                            onError={(e) => {
                              console.warn(`Error cargando imagen para ${producto.nombre}:`, logoUrl);
                              // En caso de error, mostrar fallback
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <span 
                          className={styles.serviceFallback}
                          style={{ display: logoUrl ? 'none' : 'flex' }}
                        >
                          {producto.nombre.charAt(0)}
                        </span>
                      </div>

                      <div className={styles.serviceInfo}>
                        <h3>{producto.nombre}</h3>
                        <span className={`${styles.etiqueta} ${styles[producto.etiqueta.toLowerCase()]}`}>
                          {producto.etiqueta}
                        </span>
                      </div>
                    </div>

                    <div className={styles.precio}>
                      {producto.precioOriginal && (
                        <span className={styles.precioOriginal}>
                          {formatPrice(producto.precioOriginal, producto.moneda)}
                        </span>
                      )}
                      <span className={styles.precioFinal}>
                        {formatPrice(producto.precio, producto.moneda)}
                      </span>
                    </div>

                    <p className={styles.duracion}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12,6 12,12 16,14"/>
                      </svg>
                      {producto.duracion}
                    </p>

                    <div className={styles.specs}>
                      <div className={styles.specItem}>
                        <div className={styles.specLabel}>Pantallas</div>
                        <div className={styles.specValue}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                            <circle cx="12" cy="7" r="4"/>
                          </svg>
                          {producto.pantallas}
                        </div>
                      </div>

                      <div className={styles.specItem}>
                        <div className={styles.specLabel}>Unidad</div>
                        <div className={styles.specValue}>{producto.unidad}</div>
                      </div>

                      <div className={styles.specItem}>
                        <div className={styles.specLabel}>Calidad</div>
                        <div className={styles.specValue}>{producto.calidad}</div>
                      </div>
                    </div>

                    <div className={styles.specItem}>
                      <div className={styles.specLabel}>Disponibles</div>
                      <div className={styles.specValue}>
                        {producto.disponibles}
                        {producto.disponibles <= 5 && producto.disponibles > 0 && (
                          <span style={{ color: '#f59e0b', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                            ¡Últimas!
                          </span>
                        )}
                      </div>
                    </div>

                    <ul className={styles.descripcion}>
                      {producto.descripcion.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>

                    <button
                      className={styles.addToCart}
                      onClick={() => addToCart(producto)}
                      disabled={producto.disponibles === 0}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="8" cy="21" r="1"/>
                        <circle cx="19" cy="21" r="1"/>
                        <path d="m2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>
                      </svg>
                      {producto.disponibles === 0 ? 'Agotado' : 'Añadir al Carrito'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Cart Overlay */}
        <div 
          className={`${styles.cartOverlay} ${isCartOpen ? styles.open : ''}`}
          onClick={() => setIsCartOpen(false)}
        />

        {/* Cart Sidebar */}
        <div className={`${styles.cartSidebar} ${isCartOpen ? styles.open : ''}`}>
          <div className={styles.cartHeader}>
            <h2>Carrito de Compras</h2>
            <button 
              className={styles.closeButton}
              onClick={() => setIsCartOpen(false)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div className={styles.cartContent}>
            {cart.length === 0 ? (
              <div className={styles.cartEmpty}>
                <p>Tu carrito está vacío</p>
              </div>
            ) : (
              cart.map((item) => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.cartItemHeader}>
                    <div className={styles.cartItemInfo}>
                      <h4>{item.nombre}</h4>
                      <p>{formatPrice(item.precio, item.moneda)}</p>
                      <p className={styles.cartDetail}>Por {item.duracion?.toLowerCase?.() || 'periodo'}</p>

                      <p className={styles.cartDetail}>Unidad: {item.unidad}</p>
                    </div>
                    <button
                      className={styles.removeButton}
                      onClick={() => removeFromCart(item.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                  
                  <div className={styles.quantityControls}>
                    <div className={styles.quantityButtons}>
                      <button
                        className={styles.quantityButton}
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      >-
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      </button>
                      <span className={styles.quantity}>{item.quantity}</span>
                      <button
                        className={styles.quantityButton}
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        disabled={item.quantity >= item.disponibles}
                      >+
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19"/>
                          <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                      </button>
                    </div>
                    <span className={styles.itemTotal}>
                      {formatPrice(item.precio * item.quantity, item.moneda)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {cart.length > 0 && (
            <div className={styles.cartFooter}>
              <div className={styles.total}>
                <span className={styles.totalLabel}>Total:</span>
                <span className={styles.totalAmount}>
                  {formatPrice(getTotalPrice(), currentMoneda)}
                </span>
              </div>
              <button className={styles.checkoutButton} onClick={navigateToMediosPagos}>
                Proceder al Pago
              </button>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div style={{ textAlign: 'center', marginTop: '2rem', marginBottom: '1rem', color: '#cbd5e1' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>¿Necesitas ayuda?</h3>
          <p style={{ maxWidth: '600px', margin: '0 auto', fontSize: '0.95rem', lineHeight: '1.4' }}>
            Escríbenos a través de WhatsApp para resolver tus dudas sobre compras, soporte técnico o información de nuestros servicios.
          </p>
        </div>

        <div className={styles.contactButtons}>
          <button
            className={styles.contactButton}
            onClick={() => window.open('https://wa.me/+573160789935', '_blank')}
          >
            <i className="fab fa-whatsapp"></i> Línea 1
          </button>

          <button
            className={styles.contactButton}
            onClick={() => window.open('https://wa.me/+573207499845', '_blank')}
          >
            <i className="fab fa-whatsapp"></i> Línea 2
          </button>
        </div>

        {/* Trust bar */}
        <section className={styles.trustBar}>
          <div className={styles.trustItem}>
            <svg className={styles.trustIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span>Pagos Seguros</span>
          </div>

          <div className={styles.trustItem}>
            <svg className={styles.trustIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polyline>
            </svg>
            <span>Entrega Inmediata</span>
          </div>

          <div className={styles.trustItem}>
            <svg className={styles.trustIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              <path d="M9 12l2 2 4-4"></path>
            </svg>
            <span>Garantía</span>
          </div>
        </section>

        <p className={styles.tagline}>
          Tu plataforma de confianza para servicios digitales premium
        </p>

        <footer className={styles.footer}>
          © 2025 BearMax - Todos los derechos reservados
        </footer>
      </div>
    </>
  );
};

export default PageStore;